/**
 * 기본 버튼 컴포넌트
 *
 * 디자인 시스템의 btn-* 클래스를 React 컴포넌트로 래핑한 재사용 가능한 버튼입니다.
 * forwardRef를 사용하여 DOM 접근이 가능합니다.
 *
 * @component
 * @example
 * // 기본 사용
 * <AppButton onClick={handleClick}>클릭하세요</AppButton>
 *
 * @example
 * // 다양한 스타일 조합
 * <AppButton variant="secondary" size="lg" fullWidth>
 *   대형 보조 버튼
 * </AppButton>
 *
 * @example
 * // 로딩 상태
 * <AppButton loading disabled>
 *   처리 중...
 * </AppButton>
 *
 * @typedef {object} ButtonProps
 * @property {ButtonVariant} [variant='primary'] - 버튼 스타일 ('primary' | 'secondary' | 'outline' | 'ghost')
 * @property {ButtonSize} [size='md'] - 버튼 크기 ('sm' | 'md' | 'lg')
 * @property {boolean} [fullWidth=false] - 전체 너비 사용 여부
 * @property {boolean} [loading=false] - 로딩 상태 표시 (로딩 중일 때 자동으로 disabled 됨)
 * @property {boolean} [disabled=false] - 비활성화 여부
 * @property {string} [className] - 추가 CSS 클래스
 * @property {React.ReactNode} children - 버튼 텍스트 또는 컨텐츠
 *
 * @returns {JSX.Element} 스타일이 적용된 버튼 엘리먼트
 */

import { ButtonHTMLAttributes, forwardRef } from 'react';
import { Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';

export type AppButtonVariant = 'primary' | 'secondary' | 'outline' | 'ghost';
export type AppButtonSize = 'sm' | 'md' | 'lg';

export interface AppButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: AppButtonVariant;
  size?: AppButtonSize;
  fullWidth?: boolean;
  loading?: boolean;
}

// 버튼 기본 스타일 (모든 버튼에 공통 적용)
const baseStyles =
  'inline-flex items-center justify-center gap-2 rounded-lg font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed';

// 버튼 variant별 스타일
const variantStyles: Record<AppButtonVariant, string> = {
  primary: 'bg-purple-600 text-white hover:bg-purple-700 active:bg-purple-800 focus:ring-purple-500',
  secondary: 'bg-blue-600 text-white hover:bg-blue-700 active:bg-blue-800 focus:ring-blue-500',
  outline: 'border-2 border-neutral-300 text-neutral-700 hover:bg-neutral-50 hover:border-neutral-400 focus:ring-purple-500',
  ghost: 'text-neutral-700 hover:bg-neutral-100 focus:ring-purple-500',
};

// 버튼 크기별 스타일
const sizeStyles: Record<AppButtonSize, string> = {
  sm: 'px-3 py-1.5 text-sm',
  md: 'px-4 py-2 text-base',
  lg: 'px-6 py-3 text-lg',
};

export const AppButton = forwardRef<HTMLButtonElement, AppButtonProps>(
  (
    {
      variant = 'primary',
      size = 'md',
      fullWidth = false,
      loading = false,
      disabled,
      className,
      children,
      ...props
    },
    ref
  ) => {
    return (
      <button
        ref={ref}
        disabled={disabled || loading}
        className={cn(
          baseStyles,
          variantStyles[variant],
          sizeStyles[size],
          fullWidth && 'w-full',
          className
        )}
        {...props}
      >
        {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" aria-hidden="true" />}
        {children}
      </button>
    );
  }
);

AppButton.displayName = 'AppButton';
